import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, Modal, Typography } from "@mui/material";
import type { ReactNode } from "react";
import CloseIcon from "@mui/icons-material/Close";

const POPUP_PENDING_KEY = "tradeasy_referral_popup_pending";
const POPUP_EXCLUDED_EMAILS = new Set([
  "josepm@tradeasy.tech",
  "iris@tradeasy.tech",
]);
const TARGET_TIME = new Date("2026-06-30T23:59:59").getTime();
const PROMO_URL = "https://web.tradeasy.tech/cashback-junio/";
const PROMO_IMAGE =
  "http://web.tradeasy.tech/wp-content/uploads/2026/06/20260610_junio_cashback_regalo_square.png";

const pad = (n: number) => (n < 10 ? `0${n}` : String(n));

const ReferralPromoPopup = () => {
  const [open, setOpen] = useState(false);
  const [countdownContent, setCountdownContent] = useState<ReactNode>(null);
  const shownRef = useRef(false);
  const userEmail = (localStorage.getItem("user_email") || "")
    .trim()
    .toLowerCase();
  const isExcludedUser = POPUP_EXCLUDED_EMAILS.has(userEmail);

  const canShow = useMemo(() => {
    if (Date.now() > TARGET_TIME) {
      sessionStorage.removeItem(POPUP_PENDING_KEY);
      return false;
    }

    if (isExcludedUser) {
      sessionStorage.removeItem(POPUP_PENDING_KEY);
      return false;
    }

    return sessionStorage.getItem(POPUP_PENDING_KEY) === "1";
  }, [isExcludedUser]);

  useEffect(() => {
    if (!canShow) return;

    const showPopup = () => {
      if (shownRef.current) return;
      shownRef.current = true;
      sessionStorage.removeItem(POPUP_PENDING_KEY);
      setOpen(true);
    };

    const onScroll = () => {
      if (window.scrollY > 120) showPopup();
    };

    const timer = window.setTimeout(showPopup, 2200);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [canShow]);

  useEffect(() => {
    let timeoutId: number | undefined;

    const updateCountdown = () => {
      const diff = TARGET_TIME - Date.now();

      if (diff <= 0) {
        setOpen(false);
        setCountdownContent(null);
        return;
      }

      const totalSeconds = Math.floor(diff / 1000);
      const days = Math.floor(totalSeconds / (60 * 60 * 24));
      const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
      const mins = Math.floor((totalSeconds % (60 * 60)) / 60);
      const secs = totalSeconds % 60;

      setCountdownContent(
        <>
          Termina en: <span>{pad(days)}d</span> <span>{pad(hours)}h</span>{" "}
          <span>{pad(mins)}m</span> <span>{pad(secs)}s</span>
        </>
      );

      timeoutId = window.setTimeout(updateCountdown, 1000);
    };

    updateCountdown();

    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        "& .MuiBackdrop-root": { backgroundColor: "rgba(0,0,0,0.88)" },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 640,
          borderRadius: "18px",
          px: { xs: 2.25, sm: 3.5 },
          pt: { xs: 2.25, sm: 3.5 },
          pb: { xs: 3.5, sm: 4.25 },
          textAlign: "center",
          bgcolor: "#0f172a",
          boxShadow: "0 15px 80px rgba(0,0,0,.7)",
          overflow: "hidden",
        }}
      >
        <IconButton
          onClick={() => setOpen(false)}
          sx={{
            position: "absolute",
            top: 10,
            right: 12,
            color: "#ffffff",
            width: 38,
            height: 38,
            borderRadius: "999px",
            backgroundColor: "rgba(255,255,255,.08)",
            p: 0,
            zIndex: 3,
            "&:hover": {
              backgroundColor: "rgba(255,255,255,.16)",
            },
          }}
          aria-label="Cerrar popup"
        >
          <CloseIcon sx={{ fontSize: 26 }} />
        </IconButton>

        <Box
          component="img"
          src={PROMO_IMAGE}
          alt="Llevate hasta un 5% o 500EUR de regalo"
          sx={{
            width: "100%",
            display: "block",
            maxWidth: "100%",
            height: "auto",
            mx: "auto",
            mb: 2.75,
            borderRadius: "14px",
          }}
        />

        <Typography
          sx={{
            color: "#d1d5db",
            fontSize: { xs: 16, sm: 18 },
            lineHeight: 1.5,
            mb: 2.5,
          }}
        >
          Llevate hasta un 5% de cashback o hasta 500EUR de cashback de regalo
          hasta el 30 de Junio.
        </Typography>

        <Typography
          sx={{
            color: "#e5e7eb",
            fontSize: { xs: 17, sm: 17 },
            mb: 3,
            "& span": {
              color: "#f1c440",
              fontWeight: 700,
            },
          }}
        >
          {countdownContent}
        </Typography>

        <Button
          component="a"
          href={PROMO_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setOpen(false)}
          variant="contained"
          sx={{
            width: { xs: "100%", sm: "auto" },
            boxSizing: "border-box",
            px: { xs: 2.5, sm: 4.25 },
            py: 1.875,
            borderRadius: "999px",
            bgcolor: "#f1c440",
            color: "#122b60",
            fontWeight: 800,
            fontSize: 16,
            textTransform: "none",
            transition: ".2s",
            "&:hover": {
              bgcolor: "#f1c440",
              transform: "translateY(-1px)",
              filter: "brightness(1.05)",
            },
          }}
        >
          Acceder a la promocion
        </Button>
      </Box>
    </Modal>
  );
};

export default ReferralPromoPopup;
